<script setup lang="ts">
import RuleItem from "@/components/submission/RuleItem.vue";
import Beers from "@/components/rules/Beers.vue";
import Phone from "@/components/rules/Phone.vue";
import Game from "@/components/rules/Game.vue";
import { onBeforeUnmount, onMounted, ref } from "vue";

const rulesSection = ref<HTMLElement | null>(null);
const isVisible = ref(false);
let observer: IntersectionObserver | null = null;

onMounted(() => {
  if (!rulesSection.value) {
    return;
  }

  observer = new IntersectionObserver(
      (entries: IntersectionObserverEntry[]) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            return;
          }

          isVisible.value = true;
          observer?.unobserve(entry.target);
        });
      },
      {
        threshold: 0.05,
        rootMargin: "0px 0px -5% 0px"
      }
  );

  observer.observe(rulesSection.value);
});

onBeforeUnmount(() => observer?.disconnect());
</script>

<template>
    <section id="rules" ref="rulesSection" :class="{ 'is-visible': isVisible }" class="text-center py-16 lg:pb-32 bg-default-white text-primary m-auto overflow-hidden">
        <h2 class="rules-title text-[2.5rem] lg:text-6xl text-center pb-4 lg:pb-8 text-primary">ZASADY</h2>
        <div class="rules-divider w-28 lg:w-52 h-px bg-primary/60 mx-auto mb-14"></div>
        <div class="container mx-auto px-6 lg:px-24 lg:pt-10">
            <div class="flex flex-col 2lg:flex-row gap-6 2lg:gap-4 justify-center items-center 2lg:items-end max-w-[1100px] xl:max-w-[1345px] mx-auto">
                <RuleItem :key="1" :is-visible="isVisible" item-number="1" :item-icon="Beers" item-title="Kup 2 piwa" item-content="Peroni Nastro<br/> Azzurro 0.0%" enter-delay="0s"></RuleItem>
                <div class="rule-arrow rotate-90 2lg:rotate-0 2lg:mb-20" style="--arrow-delay: 0.2s"></div>
                <RuleItem :key="2" :is-visible="isVisible" item-number="2" :item-icon="Phone" item-title="Zarejestruj kod" item-content="z Żappki<br/> na tej stronie" enter-delay="0.15s"></RuleItem>
                <div class="rule-arrow rotate-90 2lg:rotate-0 2lg:mb-20" style="--arrow-delay: 0.35s"></div>
                <RuleItem :key="3" :is-visible="isVisible" item-number="3" :item-icon="Game" item-title="Ścigaj się online" item-content="i wygraj nagrody<br/> dla fanów prędkości!" enter-delay="0.3s"></RuleItem>
            </div>
            <div class="rules-dates flex flex-col gap-1 pt-12 lg:pt-24 text-xl lg:text-2xl font-light">
                <div>Zakupu należy dokonać w okresie <span class="whitespace-nowrap text-default-red font-medium">03.06-09.06.2026</span></div>
                <div>Zgłoszenia należy dokonać w okresie <span class="whitespace-nowrap text-default-red font-medium">03.06-11.06.2026</span></div>
                <div>Organizatorem Konkursu jest <span class="font-medium">ASPE Spółka z o.o.</span></div>
            </div>
        </div>
  </section>
</template>

<style scoped>
.rules-title {
    letter-spacing: 4px;
    opacity: 0;
    transform: translateY(16px);
    transition:
        letter-spacing 0.8s ease,
        transform 0.8s ease,
        opacity 0.8s ease;
}

.rules-divider {
    opacity: 0;
    transform: scaleX(0.7);
    transform-origin: center;
    transition: transform 0.55s ease 0.12s, opacity 0.55s ease 0.12s;
}

.rule-arrow {
    --arrow-width: 33px;
    flex-shrink: 0;
    width: 0;
    height: 30px;
    opacity: 0;
    background-color: var(--color-default-red);
    clip-path: polygon(0 0, 100% 50%, 0 100%);
    transition:
        width 0.6s ease var(--arrow-delay),
        opacity 0.6s ease var(--arrow-delay);
}

.rules-dates {
    opacity: 0;
    transform: translateY(12px);
    transition: transform 0.6s ease 0.4s, opacity 0.6s ease 0.4s;
}

#rules.is-visible .rules-title {
    letter-spacing: 10px;
    opacity: 1;
    transform: translateY(0);
}

#rules.is-visible .rules-divider {
    opacity: 1;
    transform: scaleX(1);
}

#rules.is-visible .rule-arrow {
    width: var(--arrow-width);
    opacity: 1;
}

#rules.is-visible .rules-dates {
    opacity: 1;
    transform: translateY(0);
}

@media screen and (max-width: 1023px) {
    .rule-arrow {
        --arrow-width: 31px;
        height: 31px;
    }
}
</style>
